import { Building2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SidePanel } from '@/admin-area/ui/SidePanel';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import type { Supplier } from './api';
import { PanelLinksProvider } from './panel-links';
import { SupplierBody } from './SupplierPage';
import { useSupplier } from './use-supplier';

/**
 * The supplier a form refers to, opened beside the form (U1): the supplier page's parts in a side
 * panel, so the unsaved form stays as it is underneath and is there again when the panel closes.
 * Every change saved to the form's supplier comes back through `onChanged`, for the form to show and
 * send it. A link to another supplier (the one it was merged into) opens in the same panel; one to an
 * expense opens in a new tab: nothing here leaves the form.
 */
export function SupplierPanel({
  supplierId,
  name,
  onClose,
  onChanged,
  returnFocus,
}: {
  readonly supplierId: string;
  /** The name the form shows, the panel's title until the supplier is read. */
  readonly name: string;
  readonly onClose: () => void;
  readonly onChanged?: (supplier: Supplier) => void;
  /** Where focus goes when the panel closes: the name that opened it. */
  readonly returnFocus?: () => void;
}) {
  const { t } = useTranslation('suppliers');
  // The supplier shown: the form's own, or another one a link in the panel opened.
  const [shownId, setShownId] = useState(supplierId);
  const { state, reload, replace } = useSupplier(shownId);
  // The title stays the last supplier read while the next one loads.
  const [title, setTitle] = useState(name);
  const shownName = state.status === 'ready' ? state.supplier.name : null;
  useEffect(() => {
    if (shownName !== null) setTitle(shownName);
  }, [shownName]);
  // The link that switched suppliers goes away with the old one: focus waits on the panel's body.
  const body = useRef<HTMLDivElement>(null);
  const openSupplier = (id: string) => {
    setShownId(id);
    body.current?.focus();
  };

  return (
    <SidePanel
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={title}
      {...(returnFocus
        ? {
            onCloseAutoFocus: (event: Event) => {
              event.preventDefault();
              returnFocus();
            },
          }
        : {})}
    >
      <PanelLinksProvider openSupplier={openSupplier}>
        <div
          ref={body}
          tabIndex={-1}
          aria-busy={state.status === 'loading'}
          className="flex flex-col gap-4 outline-none"
        >
          {state.status === 'loading' ? (
            <SkeletonRegion label={t('page.loading')} className="flex flex-col gap-3">
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-48 w-full" />
            </SkeletonRegion>
          ) : null}
          {state.status === 'not-found' ? (
            <StateMessage
              icon={Building2}
              title={t('page.notFound.title')}
              text={t('page.notFound.text')}
            />
          ) : null}
          {state.status === 'error' ? (
            <StateMessage
              role="alert"
              tone="error"
              icon={Building2}
              title={t('page.loadError')}
              action={
                <Button type="button" variant="outline" onClick={reload}>
                  {t('page.retry')}
                </Button>
              }
            />
          ) : null}
          {state.status === 'ready' ? (
            <SupplierBody
              variant="panel"
              supplier={state.supplier}
              onReload={reload}
              onReplace={(supplier) => {
                replace(supplier);
                if (supplier.id === supplierId) onChanged?.(supplier);
              }}
            />
          ) : null}
        </div>
      </PanelLinksProvider>
    </SidePanel>
  );
}
